import {CdkVirtualScrollViewport, VirtualScrollStrategy} from "@angular/cdk/scrolling";
import {distinctUntilChanged, Observable, Subject, Subscription, throttleTime} from "rxjs";
import {ListRange} from "@angular/cdk/collections";

type ItemType = any;

export class DynamicHeightVirtualScrollStrategy implements VirtualScrollStrategy {
  private items: Array<ItemType> = [];
  private trackBy: (item: ItemType) => string;
  private defaultItemHeight: number = 0;
  private margin: number = 0;
  private itemPadding = 2;
  private heightCacheById = new Map<string, number>();
  private totalHeight: number;
  private viewport: CdkVirtualScrollViewport | null = null;
  private updateRenderedRangeSubscription: Subscription;

  private readonly RENDERED_RANGE_UPDATE_THROTTLE_MS = 100;
  private readonly scrolledIndexChangeSubject = new Subject<number>();
  private readonly updateRenderedRangeSubject = new Subject<void>();

  scrolledIndexChange: Observable<number> = this.scrolledIndexChangeSubject.asObservable().pipe(distinctUntilChanged());

  attach(viewport: CdkVirtualScrollViewport): void {
    this.viewport = viewport;
    this.totalHeight = null;
    this.initRenderedRangeSubscription();

    if (this.items) {
      this.updateTotalContentSize();
      this.updateRenderedRange();
    }
  }

  detach(): void {
    this.destroyRenderedRangeSubscription();
    this.viewport = null;
    this.totalHeight = null;
  }

  onContentScrolled(): void {
    if (this.viewport) {
      this.updateRenderedRange();
    }
  }

  onDataLengthChanged(): void {
    if (this.viewport) {
      this.updateTotalContentSize();
      this.updateRenderedRange();
    }
  }

  onContentRendered(): void {}

  onRenderedOffsetChanged(): void {}

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    if (this.viewport) {
      const offset = this.getOffsetByItemIndex(index);
      this.viewport.scrollToOffset(offset, behavior);
    }
  }

  updateItems(items: Array<ItemType>): void {
    this.items = items ?? [];
    if (this.viewport) {
      this.viewport.checkViewportSize();
      this.updateRenderedRange();
    }
  }

  setDefaultItemHeight(defaultHeight: number): void {
    this.defaultItemHeight = defaultHeight;
  }

  setMargin(margin: number): void {
    this.margin = margin;
  }

  setItemPadding(n: number): void {
    this.itemPadding = n;
  }

  setTrackBy(trackBy: (item: ItemType) => string): void {
    this.trackBy = trackBy;
  }

  updateHeightCache(heightById: Map<string, number>): void {
    let changed = false;
    let heightDelta = 0;
    heightById.forEach((height, id) => {
      const outerHeight = height + this.margin;
      const previousHeight = this.heightCacheById.get(id);
      const delta = outerHeight - previousHeight;
      changed ||= delta !== 0;
      heightDelta += delta;
      if (delta) {
        this.heightCacheById.set(id, outerHeight);
      }
    });

    if (heightDelta) {
      this.updateTotalContentSize(heightDelta);
    }

    // Even if the overall height did not change, some items might have increased in height, while others decreased,
    // so if there were any changes, need to update rendered range
    if (changed) {
      this.updateRenderedRange();
    }
  }

  private getItemHeight(item: ItemType): number {
    const itemId = this.trackBy(item);
    let height = this.heightCacheById.get(itemId);
    if (height == null) {
      height = this.defaultItemHeight;
      this.heightCacheById.set(itemId, height);
    }
    return height;
  }

  private measureItemHeight(items: Array<ItemType>): number {
    return items.reduce((acc, item) => acc + this.getItemHeight(item), 0);
  }

  private getTotalHeight() {
    const height = this.measureItemHeight(this.items);
    // Cater for the bottom margin of the last item to be folded
    return this.margin && height > this.margin ? height - this.margin : height;
  }

  private getOffsetByItemIndex(index: number): number {
    return this.measureItemHeight(this.items.slice(0, index));
  }

  private getItemIndexByOffset(offset: number): number {
    let accumulatedOffset = 0;
    for (let i = 0; i < this.items?.length; i++) {
      const item = this.items[i]
      accumulatedOffset += this.getItemHeight(item);
      if (accumulatedOffset >= offset) {
        return i;
      }
    }

    return 0;
  }

  private determineItemCountInViewport(startIndex: number): number {
    if (this.viewport && this.items?.length) {
      let totalSize = 0;
      const viewportSize = this.viewport.getViewportSize();

      let i: number;
      for (i = startIndex; i < this.items.length; i++) {
        totalSize += this.getItemHeight(this.items[i]);
        if (totalSize >= viewportSize) {
          break;
        }
      }

      return i - startIndex + 1;
    } else {
      return 0;
    }
  }

  private updateRenderedRange() {
    this.updateRenderedRangeSubject.next();
  };

  private calculateAndUpdateRenderedRange(): void {
    if (this.viewport) {
      const scrollOffset = this.viewport.measureScrollOffset();
      const dataLength = this.viewport.getDataLength();
      const scrollIndex = this.getItemIndexByOffset(scrollOffset);
      const start = Math.max(0, scrollIndex - this.itemPadding);
      const end = Math.min(dataLength, scrollIndex + this.determineItemCountInViewport(scrollIndex) + this.itemPadding);
      const range: ListRange = {start, end};
      const offsetByItemIndex = this.getOffsetByItemIndex(range.start);

      this.viewport.setRenderedRange(range);
      this.scrolledIndexChangeSubject.next(scrollIndex);
      this.viewport.setRenderedContentOffset(offsetByItemIndex);
    }
  }

  private updateTotalContentSize(heightDelta?: number) {
    this.totalHeight = this.totalHeight != null && heightDelta != null ? this.totalHeight + heightDelta : this.getTotalHeight();
    this.viewport.setTotalContentSize(this.totalHeight);
  }

  private initRenderedRangeSubscription(): void {
    this.destroyRenderedRangeSubscription();
    this.updateRenderedRangeSubscription = this.updateRenderedRangeSubject
      .pipe(throttleTime(this.RENDERED_RANGE_UPDATE_THROTTLE_MS, undefined, {leading: false, trailing: true}))
      .subscribe(() => this.calculateAndUpdateRenderedRange());
  }

  private destroyRenderedRangeSubscription(): void {
    this.updateRenderedRangeSubscription?.unsubscribe();
    this.updateRenderedRangeSubscription = null;
  }
}
